import { STATUSPAGE } from '@repo/ui';
import { SIGNATURE_STATUS } from 'domain/models';
import {
  SummaryOfTerminationTermsUseCaseInterface,
  TceSummaryBySituationUseCaseInterface,
} from 'domain/usecases';
import { TCE_LISTING_TYPE } from 'domain/value-objects/tce-listing-type';
import { useFormik } from 'formik';
import { RequestState } from 'presentation/app/common/interfaces/request-state';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadCoursesUser } from 'presentation/config/store/courses/courses-slice';
import { loadGroupsUser } from 'presentation/config/store/groups/groups-slice';
import {
  setLoadingReportSelectCourse,
  setLoadingReportSelectOrganization,
} from 'presentation/config/store/load-select-report/loadingSelectReportSlice';
import { loadOrganizationsUser } from 'presentation/config/store/organizations/organizations-slice';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

interface TceSummaryInitialFilters {
  entity?: Array<{ label: string; value: string }>;
  course?: Array<{ label: string; value: string }>;
  group?: Array<{ label: string; value: string }>;
  local?: Array<{ label: string; value: string }>;
  studentName?: string;
  studentCpf?: string;
}

export const useTceSummary = (initialFilters?: TceSummaryInitialFilters) => {
  const navigate = useNavigate();
  const {
    groupService,
    courseService,
    tceSummaryService,
    organizationService,
    internshipLocationService,
  } = useService();

  // hooks
  const dispatch = useDispatch();
  const { groupsUser } = useAppSelector((state) => state.groups);
  const { coursesUser } = useAppSelector((state) => state.courses);
  const { organizationsUser } = useAppSelector((state) => state.organizations);
  const { loadingReportSelectCourse, loadingReportSelectOrganization } =
    useAppSelector((state) => state.loadingSelectReport);

  // states
  const [summaryOfTerminationTerms, setSummaryOfTerminationTerms] = useState<
    RequestState<SummaryOfTerminationTermsUseCaseInterface.Result>
  >({
    data: undefined,
    status: STATUSPAGE.INITIAL,
  });

  const [tceSummaryBySituation, setTceSummaryBySituation] = useState<
    RequestState<TceSummaryBySituationUseCaseInterface.Result>
  >({
    data: undefined,
    status: STATUSPAGE.INITIAL,
  });

  const [
    internshipLocationsSelectOptions,
    setInternshipLocationsSelectOptions,
  ] = useState<Array<{ id: string; nome: string }>>([]);
  const [loadingInternshipLocations, setLoadingInternshipLocations] =
    useState(false);

  const shouldRenderTceCountTable = useMemo(() => {
    if (!tceSummaryBySituation.data) return false;

    return !Object.values(tceSummaryBySituation.data).every(
      (value) => value === 0,
    );
  }, [tceSummaryBySituation.data]);

  const shouldRenderTerminationTermCountTable = useMemo(() => {
    if (!summaryOfTerminationTerms.data) return false;

    return !Object.values(summaryOfTerminationTerms.data).every(
      (value) => value === 0,
    );
  }, [summaryOfTerminationTerms.data]);

  const requestSummaryOfTerminationTerms = useCallback(
    (params: SummaryOfTerminationTermsUseCaseInterface.Params) => {
      setSummaryOfTerminationTerms((prevState) => ({
        ...prevState,
        status: STATUSPAGE.LOADING,
      }));
      tceSummaryService
        .summaryOfTerminationTerms(params)
        .then((res) => {
          setSummaryOfTerminationTerms((prevState) => ({
            ...prevState,
            data: res,
          }));
        })
        .catch(() => {
          setSummaryOfTerminationTerms((prevState) => ({
            ...prevState,
            data: undefined,
          }));
        })
        .finally(() => {
          setSummaryOfTerminationTerms((prevState) => ({
            ...prevState,
            status: STATUSPAGE.DONE,
          }));
        });
    },
    [],
  );

  const requestTceSummaryBySituation = useCallback(
    (params: TceSummaryBySituationUseCaseInterface.Params) => {
      setTceSummaryBySituation((prevState) => ({
        ...prevState,
        status: STATUSPAGE.LOADING,
      }));
      tceSummaryService
        .tceSummaryBySituation(params)
        .then((res) => {
          setTceSummaryBySituation((prevState) => ({
            ...prevState,
            data: res,
          }));
        })
        .catch(() => {
          setTceSummaryBySituation((prevState) => ({
            ...prevState,
            data: undefined,
          }));
        })
        .finally(() => {
          setTceSummaryBySituation((prevState) => ({
            ...prevState,
            status: STATUSPAGE.DONE,
          }));
        });
    },
    [],
  );

  const requestGetAllCoursesUser = useCallback(async (entityId?: string) => {
    dispatch(setLoadingReportSelectCourse(true));

    await courseService
      .getAllCoursesUser({ entityId })
      .then((res) => {
        dispatch(loadCoursesUser(res));
      })
      .catch((err) => console.log(err))
      .finally(() => {
        dispatch(setLoadingReportSelectCourse(false));
      });
  }, []);

  const requestGetAllOrganizationsUser = useCallback(async () => {
    dispatch(setLoadingReportSelectOrganization(true));
    await organizationService
      .getAllOrganizationsUser()
      .then((res) => dispatch(loadOrganizationsUser(res)))
      .catch((err) => console.log(err))
      .finally(() => {
        dispatch(setLoadingReportSelectOrganization(false));
      });
  }, []);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      course: initialFilters?.course || [],
      entity: initialFilters?.entity || [],
      group: initialFilters?.group || [],
      local: initialFilters?.local || [],
      studentName: initialFilters?.studentName || '',
      studentCpf: initialFilters?.studentCpf || '',
    },
    validationSchema: Yup.object({
      course: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string().nullable(),
            value: Yup.string().nullable(),
          })
          .nullable(),
      ),
      entity: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string().nullable(),
            value: Yup.string().nullable(),
          })
          .nullable(),
      ),
      group: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string().nullable(),
            value: Yup.string().nullable(),
          })
          .nullable(),
      ),
      local: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string().nullable(),
            value: Yup.string().nullable(),
          })
          .nullable(),
      ),
      studentName: Yup.string(),
      studentCpf: Yup.string(),
    }),

    onSubmit: (values) => {
      const courseValues = values?.course?.map((course) => course?.value);
      const entityValues = values?.entity?.map((entity) => entity?.value);
      const groupValues = values?.group?.map((group) => group?.value);
      const localValues = values?.local?.map((local) => local?.value);

      const filterParams = {
        courseHashIds: courseValues || [],
        organizationHashIds: entityValues || [],
        groups: groupValues && groupValues.length > 0 ? groupValues : undefined,
        internshipLocations:
          localValues && localValues.length > 0 ? localValues : undefined,
        studentName: values?.studentName || undefined,
        cpf: values?.studentCpf || undefined,
      };

      requestSummaryOfTerminationTerms(filterParams);
      requestTceSummaryBySituation(filterParams);
    },
  });

  const requestGetAllGroupsUser = useCallback(async () => {
    try {
      const groups = await groupService.getAllGroupsUser();
      dispatch(loadGroupsUser(groups));
    } catch {}
  }, [dispatch, groupService]);

  const requestGetInternshipLocations = useCallback(async () => {
    setLoadingInternshipLocations(true);
    try {
      const locations = await internshipLocationService.getInternshipLocation({
        courseHashIds: validation.values.course?.map((c) => c.value),
        organizationHashIds: validation.values.entity?.map((e) => e.value),
        groups: validation.values.group?.map((g) => g.value),
      });
      setInternshipLocationsSelectOptions(
        locations.map((loc) => ({ id: loc.id, nome: loc.name })),
      );
    } catch {
    } finally {
      setLoadingInternshipLocations(false);
    }
  }, [
    internshipLocationService,
    validation.values.course,
    validation.values.entity,
    validation.values.group,
  ]);

  const tceCountRows = useMemo(() => {
    return [
      {
        id: 'studentsWithoutTce',
        statusTitle: 'Estudantes sem TCE',
        statusSubtitle: 
        'Estudantes ativos no grupo que ainda não iniciaram o processo de preenchimento do TCE.',
        icon: {
          bgColor: '#DDF6FB',
          class: 'ri-group-line',
          innerColor: '#1AB0CE',
        },
        count: tceSummaryBySituation?.data?.studentsWithoutTce ?? 0,
        onClick: () => {
          navigate('/tce/listagem', {
            state: {
              listingType: TCE_LISTING_TYPE.STUDENTS_WITHOUT_TCE,
              situationTitle: 'Estudantes sem TCE',
              filters: validation.values,
            },
          });
        },
      },
      {
        id: 'drafts',
        statusTitle: 'Rascunhos',
        statusSubtitle:
          'TCE iniciado pela instituição e pendente de preenchimento e/ou assinatura do estudante; ou TCE iniciado pelo estudante, aguardando finalização do preenchimento e/ou assinatura.',
        icon: {
          bgColor: '#E8ECF4 ',
          class: 'ri-sticky-note-line',
          innerColor: '#5A6583',
        },
        count: tceSummaryBySituation?.data?.drafts ?? 0,
        onClick: () => {
          navigate('/tce/listagem', {
            state: {
              listingType: TCE_LISTING_TYPE.DRAFTS,
              situationTitle: 'Rascunhos',
              filters: validation.values,
            },
          });
        },
      },
      {
        id: 'waitingStudentSignature',
        statusTitle: 'Aguardando Assinatura do Estudante',
        statusSubtitle:
          'TCE preenchido, mas aguardando a assinatura do estudante.',
        icon: {
          bgColor: '#FDF1E9 ',
          class: 'ri-pencil-fill',
          innerColor: '#F19C59',
        },
        count: tceSummaryBySituation?.data?.waitingStudentSignature ?? 0,
        onClick: () => {
          navigate('/tce/listagem', {
            state: {
              listingType: TCE_LISTING_TYPE.WAITING_SIGNATURE,
              situationTitle: 'Aguardando Assinatura do Estudante',
              filters: validation.values,
              signatureStatus: SIGNATURE_STATUS.STUDENT,
            },
          });
        },
      },
      {
        id: 'waitingGrantorSignature',
        statusTitle: 'Aguardando Assinatura do Concedente',
        statusSubtitle:
          'TCE preenchido, mas aguardando a assinatura do concedente de estágio.',
        icon: {
          bgColor: '#FDF1E9 ',
          class: 'ri-pencil-line',
          innerColor: '#F19C59',
        },
        count: tceSummaryBySituation?.data?.waitingGrantorSignature ?? 0,
        onClick: () => {
          navigate('/tce/listagem', {
            state: {
              listingType: TCE_LISTING_TYPE.WAITING_SIGNATURE,
              situationTitle: 'Aguardando Assinatura do Concedente',
              filters: validation.values,
              signatureStatus: SIGNATURE_STATUS.GRANTOR,
            },
          });
        },
      },
      {
        id: 'waitingInstitutionSignature',
        statusTitle: 'Aguardando Assinatura da Instituição',
        statusSubtitle:
          'TCE preenchido, mas aguardando a assinatura da instituição de ensino.',
        icon: {
          bgColor: '#FDF1E9 ',
          class: 'ri-edit-line',
          innerColor: '#F19C59',
        },
        count: tceSummaryBySituation?.data?.waitingInstitutionSignature ?? 0,
        onClick: () => {
          navigate('/tce/listagem', {
            state: {
              listingType: TCE_LISTING_TYPE.WAITING_SIGNATURE,
              situationTitle: 'Aguardando Assinatura da Instituição',
              filters: validation.values,
              signatureStatus: SIGNATURE_STATUS.INSTITUTION,
            },
          });
        },
      },
      {
        id: 'integrative',
        statusTitle: 'Integradora',
        statusSubtitle:
          'TCE preenchido externamente, com posterior importação para o sistema Rade.',
        icon: {
          bgColor: '#FDF3D6',
          class: 'ri-file-text-line',
          innerColor: '#F2C94C',
        },
        count: tceSummaryBySituation?.data?.integrative ?? 0,
        onClick: () => {
          navigate('/tce/listagem', {
            state: {
              listingType: TCE_LISTING_TYPE.INTEGRATIVE,
              situationTitle: 'Integradora',
              filters: validation.values,
            },
          });
        },
      },
      {
        id: 'active',
        statusTitle: 'Ativos',
        statusSubtitle:
          'TCE concluído, assinado por todas as partes e em vigor, indicando que o estágio está em andamento.',
        icon: {
          bgColor: '#E0FBF4',
          class: 'ri-check-line',
          innerColor: '#27AE60',
        },
        count: tceSummaryBySituation?.data?.active ?? 0,
        onClick: () => {
          navigate('/tce/listagem', {
            state: {
              listingType: TCE_LISTING_TYPE.ACTIVE,
              situationTitle: 'Ativos',
              filters: validation.values,
            },
          });
        },
      },
      {
        id: 'expired',
        statusTitle: 'Expirados',
        statusSubtitle:
          'TCEs que já passaram da data de término, ou seja, não estão mais em vigor e não podem ser utilizados para validar estágios.',
        icon: {
          bgColor: '#FDEBEC',
          class: 'ri-alert-line',
          innerColor: '#EB5757',
        },
        count: tceSummaryBySituation?.data?.expired ?? 0,
        onClick: () => {
          navigate('/tce/listagem', {
            state: {
              listingType: TCE_LISTING_TYPE.EXPIRED,
              situationTitle: 'Expirados',
              filters: validation.values,
            },
          });
        },
      },
    ];
  }, [tceSummaryBySituation?.data, validation.values, navigate]);

  const terminationTermCountRows = useMemo(() => {
    return [
      {
        id: 'waitingGrantor',
        statusTitle: 'Aguardando Concedente',
        statusSubtitle:
          'termo(s) de rescisão aguardando ciência do concedente do estágio.',
        icon: {
          class: 'ri-edit-line',
        },
        count: summaryOfTerminationTerms?.data?.waitingGrantor ?? 0,
        onClick: () => {
          navigate('/tce/listagem', {
            state: {
              listingType: TCE_LISTING_TYPE.RESCISSION_TERMS_IN_PROGRESS,
              situationTitle: 'Aguardando Concedente',
              filters: validation.values,
            },
          });
        },
      },
      {
        id: 'waitingInstitution',
        statusTitle: 'Aguardando Instituição',
        statusSubtitle:
          'termo(s) de rescisão aguardando ciência da instituição.',
        icon: {
          class: 'ri-edit-box-line',
        },
        count: summaryOfTerminationTerms?.data?.waitingInstitution ?? 0,
        onClick: () => {
          navigate('/tce/listagem', {
            state: {
              listingType: TCE_LISTING_TYPE.RESCISSION_TERMS_IN_PROGRESS,
              situationTitle: 'Aguardando Instituição',
              filters: validation.values,
            },
          });
        },
      },
      {
        id: 'concluded',
        statusTitle: 'Concluídos',
        statusSubtitle: 'termo(s) de rescisão concluídos por todas as partes.',
        icon: {
          class: 'ri-check-double-line',
        },
        count: summaryOfTerminationTerms?.data?.concluded ?? 0,
        onClick: () => {
          navigate('/tce/listagem', {
            state: {
              listingType: TCE_LISTING_TYPE.RESCISSION_TERMS_CONCLUDED,
              situationTitle: 'Concluídos',
              filters: validation.values,
            },
          });
        },
      },
    ];
  }, [summaryOfTerminationTerms?.data, validation.values, navigate]);

  return {
    requestGetAllCoursesUser,
    requestTceSummaryBySituation,
    requestGetAllOrganizationsUser,
    requestSummaryOfTerminationTerms,
    requestGetAllGroupsUser,
    requestGetInternshipLocations,
    validation,
    coursesUser,
    groupsUser,
    tceCountRows,
    organizationsUser,
    tceSummaryBySituation,
    terminationTermCountRows,
    loadingReportSelectCourse,
    summaryOfTerminationTerms,
    shouldRenderTceCountTable,
    loadingReportSelectOrganization,
    shouldRenderTerminationTermCountTable,
    internshipLocationsSelectOptions,
    loadingInternshipLocations,
  };
};
